import { apiClient } from '../../apiHelper';
import type { AppointmentTypeValue } from '../appointment/appointment';

export type FacilityProcedure = {
  id: number;
  facility_id: string;
  code?: string | null;
  description: string;
  appointment_type?: AppointmentTypeValue | string | null;
  is_visit_type?: boolean;
  active: boolean;
  created_at?: string;
  updated_at?: string | null;
};

function unwrapList(res: any): FacilityProcedure[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
}

export const facilityProcedureService = {
  list: async (
    activeOnly = false,
    visitTypesOnly = false
  ): Promise<FacilityProcedure[]> => {
    // apiClient.get expects flat query params as the 2nd arg (not Axios `{ params }`).
    const res: any = await apiClient.get('/facility-procedures', {
      active_only: activeOnly,
      visit_types_only: visitTypesOnly,
    });
    return unwrapList(res);
  },

  ensure: async (
    items: Array<{ description: string; code?: string | null }>
  ): Promise<{ data: FacilityProcedure[]; created: number; existing: number }> => {
    const res: any = await apiClient.post('/facility-procedures/ensure', {
      items: items.filter((i) => (i.description || '').trim()),
    });
    const body =
      res?.data && !Array.isArray(res.data) && res.created == null
        ? res.data
        : res;
    return {
      data: Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body)
          ? body
          : [],
      created: Number(body?.created || 0),
      existing: Number(body?.existing || 0),
    };
  },

  createCategory: async (payload: {
    description: string;
    code?: string;
  }): Promise<FacilityProcedure> => {
    // Backend promotes an existing MWL row with the same description
    // (spaces/underscores/case-insensitive) instead of inserting a duplicate.
    const res: any = await apiClient.post('/facility-procedures', {
      description: payload.description,
      code: payload.code,
      is_visit_type: true,
      active: true,
    });
    return (res?.data || res) as FacilityProcedure;
  },

  /** Promote an unclassified MWL procedure into a bookable visit category. */
  promoteToCategory: async (id: number): Promise<FacilityProcedure> => {
    const res: any = await apiClient.patch(`/facility-procedures/${id}`, {
      is_visit_type: true,
    });
    return (res?.data || res) as FacilityProcedure;
  },

  create: async (payload: {
    description: string;
    code?: string;
    appointment_type?: string | null;
    is_visit_type?: boolean;
    active?: boolean;
  }) => {
    const res: any = await apiClient.post('/facility-procedures', payload);
    return res?.data || res;
  },

  update: async (
    id: number,
    payload: {
      description?: string;
      code?: string | null;
      appointment_type?: string | null;
      is_visit_type?: boolean;
      active?: boolean;
    }
  ) => {
    const res: any = await apiClient.patch(`/facility-procedures/${id}`, payload);
    return res?.data || res;
  },
};
